import { createClient } from "@supabase/supabase-js";

let supabaseClient = null;

const API_VIEW_NAME_OVERRIDES = {
  client_error_reports: "v_client_error_reports",
};

const API_RPC_NAMES = new Set([
  "accept_invite",
  "add_conversation_members",
  "archive_hierarchy",
  "auth_resolve",
  "apply_ai_citation_review",
  "change_history_page",
  "change_history_users",
  "correct_evidence_anchor",
  "claim_enterprise_contract_owner",
  "claim_enterprise_contract_owner_for_user",
  "conversation_members",
  "conversation_messages",
  "conversation_messages_search",
  "conversations_inbox",
  "conversations_search",
  "dashboard_work_queue",
  "create_conversation",
  "create_evidence_anchor",
  "create_reference_import_job",
  "finalize_reference_import_file",
  "create_invite",
  "create_source_revision",
  "create_team",
  "delete_team",
  "delete_hierarchy",
  "ensure_org_contract",
  "get_invite_public",
  "log_event",
  "mark_conversation_read",
  "move_team_member",
  "org_seat_count",
  "pins_detailed",
  "post_conversation_message",
  "profile_lookup_by_email_ci",
  "restore_hierarchy",
  "remove_team_member",
  "rename_team",
  "resolve_reference_import_file",
  "revoke_invite",
  "save_annotations_batch",
  "save_annotation_revision",
  "save_claim_revision",
  "update_reference_import_file",
  "retry_reference_import_file",
  "cancel_reference_import_job",
  "get_billing_entitlements",
  "get_billing_checkout_binding",
  "prepare_billing_checkout",
  "bind_billing_checkout",
  "record_billing_usage",
  "reconcile_billing_webhook",
  "search_items",
  "transition_asset_lifecycle",
  "fetch_asset_lifecycle_impact",
  "fetch_asset_lifecycle_history",
  "transition_source_revision_validity",
  "transition_claim_revision_validity",
  "materialize_due_revision_expiry",
  "fetch_revision_validity_history",
  "fetch_revision_validity_impact",
  "get_external_review_invite",
  "accept_external_reviewer_invite",
  "fetch_external_review_context",
  "external_reviewer_search",
  "external_reviewer_storage_access",
  "external_reviewer_add_comment",
  "external_reviewer_submit_decision",
  "create_external_reviewer_capability",
  "list_external_reviewer_capabilities",
  "get_external_reviewer_management_scope",
  "revoke_external_reviewer_capability",
  "create_outbound_connection",
  "list_outbound_connections",
  "revoke_outbound_connection",
  "create_outbound_recipient_mapping",
  "list_outbound_recipient_mappings",
  "enqueue_outbound_delivery",
  "list_outbound_delivery_jobs",
  "list_outbound_delivery_events",
  "heartbeat_outbound_delivery_job",
  "finalize_outbound_delivery_job",
  "retry_outbound_delivery_job",
  "revoke_outbound_delivery_job",
  "reconcile_outbound_delivery_job",
  "list_outbound_delivery_targets",
  "set_outbound_connection_secret",
  "expire_outbound_connections",
  "session_context",
  "set_org_member_role",
  "set_team_member_role",
  "tag_candidates",
  "team_activity",
  "toggle_conversation_message_reaction",
]);

function getApiSchemaClient(client) {
  if (typeof client?.schema !== "function") {
    return client;
  }
  return client.schema("api");
}

function getApiViewName(name) {
  if (typeof name !== "string" || !name) return null;
  if (API_VIEW_NAME_OVERRIDES[name]) return API_VIEW_NAME_OVERRIDES[name];
  if (name.startsWith("v_")) return name;
  return null;
}

function shouldUseApiRpc(name) {
  if (typeof name !== "string" || !name) return false;
  return API_RPC_NAMES.has(name) || name.startsWith("admin_");
}

function createSupabaseClient() {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const missingEnv = [];
  if (!supabaseUrl) missingEnv.push("REACT_APP_SUPABASE_URL");
  if (!supabaseKey) missingEnv.push("REACT_APP_SUPABASE_ANON_KEY");
  if (missingEnv.length) {
    const message = `Missing Supabase env vars: ${missingEnv.join(", ")}.`;
    // eslint-disable-next-line no-console
    console.warn(message);
    throw new Error(message);
  }

  return createClient(supabaseUrl, supabaseKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
}

export function getSupabaseClient() {
  if (!supabaseClient) {
    supabaseClient = createSupabaseClient();
  }
  return supabaseClient;
}

export function setSupabaseClientForTests(client) {
  supabaseClient = client;
}

export function resetSupabaseClientForTests() {
  supabaseClient = null;
}

export const supabase = new Proxy(
  {},
  {
    get(_target, prop) {
      const client = getSupabaseClient();
      if (prop === "from") {
        return (name) => {
          const apiViewName = getApiViewName(name);
          if (apiViewName) {
            const apiClient = getApiSchemaClient(client);
            if (typeof apiClient?.from === "function") {
              return apiClient.from(apiViewName);
            }
          }
          return client.from(name);
        };
      }
      if (prop === "rpc") {
        return (name, args) => {
          if (shouldUseApiRpc(name)) {
            const apiClient = getApiSchemaClient(client);
            if (typeof apiClient?.rpc === "function") {
              return apiClient.rpc(name, args);
            }
          }
          return client.rpc(name, args);
        };
      }
      const value = client[prop];
      if (typeof value === "function") {
        return value.bind(client);
      }
      return value;
    },
  },
);
